import {
  TYPE_ROLES
} from "../chunk-JQGFMWOO.js";
import {
  FontFamilyNameSchema
} from "../chunk-6KUQYVPT.js";

// src/schemas/slide-content.ts
import { Type as Type9 } from "@sinclair/typebox";

// src/schemas/slide-content/text.ts
import { Type as Type3 } from "@sinclair/typebox";

// src/schemas/slide-content/common.ts
import { Type as Type2 } from "@sinclair/typebox";

// src/schemas/slide-content/theme.ts
import { Type } from "@sinclair/typebox";
var HexColorSchema = Type.String({
  pattern: "^#?[0-9A-Fa-f]{6}$",
  description: "Hex color (e.g. #FF0000)"
});
var SEMANTIC_COLOR_NAMES = [
  "primary",
  "secondary",
  "accent",
  "background",
  "text",
  "text2",
  "background2",
  "accent4",
  "accent5",
  "accent6"
];
var SEMANTIC_COLOR_ALIASES = [
  "accent1",
  "accent2",
  "accent3",
  "tx1",
  "tx2",
  "bg1",
  "bg2"
];
var ColorValueSchema = Type.Union(
  [
    HexColorSchema,
    ...SEMANTIC_COLOR_NAMES.map((name) => Type.Literal(name)),
    ...SEMANTIC_COLOR_ALIASES.map((name) => Type.Literal(name))
  ],
  { description: "Hex color or semantic theme color name" }
);
var STYLE_NAMES = [
  "title",
  "subtitle",
  "heading1",
  "heading2",
  "heading3",
  "body",
  "caption",
  ...TYPE_ROLES
];
var StyleNameSchema = Type.Union(
  STYLE_NAMES.map((name) => Type.Literal(name)),
  { description: "Predefined style name" }
);

// src/schemas/slide-content/common.ts
var PptxAlignmentSchema = Type2.Union(
  [Type2.Literal("left"), Type2.Literal("center"), Type2.Literal("right")],
  { description: "Horizontal alignment options" }
);
var VerticalAlignmentSchema = Type2.Union(
  [Type2.Literal("top"), Type2.Literal("middle"), Type2.Literal("bottom")],
  { description: "Vertical alignment options" }
);
var ShadowSchema = Type2.Object(
  {
    type: Type2.Optional(
      Type2.Union([Type2.Literal("outer"), Type2.Literal("inner")], {
        description: "Shadow type"
      })
    ),
    color: Type2.Optional(ColorValueSchema),
    blur: Type2.Optional(
      Type2.Number({ description: "Shadow blur radius in points" })
    ),
    offset: Type2.Optional(
      Type2.Number({ description: "Shadow offset in points" })
    ),
    angle: Type2.Optional(
      Type2.Number({ description: "Shadow angle in degrees" })
    ),
    opacity: Type2.Optional(
      Type2.Number({
        minimum: 0,
        maximum: 1,
        description: "Shadow opacity (0-1)"
      })
    )
  },
  {
    description: "Shadow configuration",
    additionalProperties: false
  }
);
var GridPositionSchema = Type2.Object(
  {
    column: Type2.Number({
      minimum: 0,
      description: "Starting column (0-indexed)"
    }),
    row: Type2.Number({ minimum: 0, description: "Starting row (0-indexed)" }),
    columnSpan: Type2.Optional(
      Type2.Number({
        minimum: 1,
        description: "Number of columns to span (default: 1)"
      })
    ),
    rowSpan: Type2.Optional(
      Type2.Number({
        minimum: 1,
        description: "Number of rows to span (default: 1)"
      })
    )
  },
  { additionalProperties: false, description: "Grid-based positioning" }
);

// src/schemas/slide-content/text.ts
var TextRunSchema = Type3.Object(
  {
    text: Type3.String({ description: "Run text content" }),
    color: Type3.Optional(
      Type3.String({
        description: "Run color (hex without # or semantic theme name)"
      })
    ),
    bold: Type3.Optional(Type3.Boolean({ description: "Bold run" })),
    fontWeight: Type3.Optional(
      Type3.Integer({
        minimum: 100,
        maximum: 900,
        description: "Per-run weight (100\u2013900). Overrides `bold` when set \u2014 renderer picks the closest embedded variant via CSS font-matching."
      })
    ),
    italic: Type3.Optional(Type3.Boolean({ description: "Italic run" })),
    underline: Type3.Optional(
      Type3.Union([
        Type3.Boolean({ description: "Simple underline toggle" }),
        Type3.Object(
          {
            style: Type3.Optional(
              Type3.Union([
                Type3.Literal("sng"),
                Type3.Literal("dbl"),
                Type3.Literal("dash"),
                Type3.Literal("dotted")
              ])
            ),
            color: Type3.Optional(
              Type3.String({ description: "Underline color (hex)" })
            )
          },
          { additionalProperties: false }
        )
      ])
    ),
    strike: Type3.Optional(Type3.Boolean({ description: "Strikethrough run" })),
    fontSize: Type3.Optional(
      Type3.Number({ minimum: 1, description: "Run font size in points" })
    ),
    fontFace: Type3.Optional(Type3.String({ description: "Run font family" })),
    superscript: Type3.Optional(
      Type3.Boolean({ description: "Render run as superscript" })
    ),
    subscript: Type3.Optional(
      Type3.Boolean({ description: "Render run as subscript" })
    ),
    charSpacing: Type3.Optional(
      Type3.Number({ description: "Character spacing in points" })
    ),
    breakLine: Type3.Optional(
      Type3.Boolean({ description: "Insert line break after this run" })
    )
  },
  {
    description: "A styled text run. Run options override component-level defaults.",
    additionalProperties: false
  }
);
var TextPropsSchema = Type3.Object(
  {
    text: Type3.Optional(
      Type3.String({
        description: "Text content to display. Mutually exclusive with `runs` \u2014 set exactly one of the two."
      })
    ),
    runs: Type3.Optional(
      Type3.Array(TextRunSchema, {
        minItems: 1,
        description: "Rich text runs with per-run styling, rendered as one text block. Mutually exclusive with `text` \u2014 set exactly one of the two. Component-level props (align, valign, fill, lineSpacing, position\u2026) still apply to the whole block."
      })
    ),
    x: Type3.Optional(
      Type3.Union([
        Type3.Number({ description: "X position in inches" }),
        Type3.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "X as percentage"
        })
      ])
    ),
    y: Type3.Optional(
      Type3.Union([
        Type3.Number({ description: "Y position in inches" }),
        Type3.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Y as percentage"
        })
      ])
    ),
    w: Type3.Optional(
      Type3.Union([
        Type3.Number({ description: "Width in inches" }),
        Type3.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Width as percentage"
        })
      ])
    ),
    h: Type3.Optional(
      Type3.Union([
        Type3.Number({ description: "Height in inches" }),
        Type3.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Height as percentage"
        })
      ])
    ),
    fit: Type3.Optional(
      Type3.Object(
        {
          maxLines: Type3.Optional(
            Type3.Integer({
              minimum: 1,
              description: "Lines the text may occupy, estimated with the width model at the effective font size."
            })
          ),
          shrink: Type3.Optional(
            Type3.Array(Type3.Number({ minimum: 1 }), {
              minItems: 1,
              description: "Readable font sizes to try in order when the text exceeds maxLines or the box height. The first that fits is used."
            })
          )
        },
        {
          additionalProperties: false,
          description: "Bounded fit: estimate the lines at the effective size, step down only through the declared readable sizes, and fail generation with text_fit_overflow when nothing fits. Without maxLines the box height bounds the text; without shrink the text must fit as sized."
        }
      )
    ),
    fontSize: Type3.Optional(
      Type3.Number({ minimum: 1, description: "Font size in points" })
    ),
    fontFace: Type3.Optional(FontFamilyNameSchema),
    color: Type3.Optional(
      Type3.String({ description: 'Text color (hex without #, e.g., "FF0000")' })
    ),
    bold: Type3.Optional(Type3.Boolean({ description: "Bold text" })),
    fontWeight: Type3.Optional(
      Type3.Integer({
        minimum: 100,
        maximum: 900,
        description: "Per-run weight (100\u2013900). Renderer picks the closest embedded variant via CSS font-matching and references it through a synthetic family alias. Overrides `bold` when set."
      })
    ),
    italic: Type3.Optional(Type3.Boolean({ description: "Italic text" })),
    underline: Type3.Optional(
      Type3.Union([
        Type3.Boolean({ description: "Simple underline toggle" }),
        Type3.Object(
          {
            style: Type3.Optional(
              Type3.Union([
                Type3.Literal("sng"),
                Type3.Literal("dbl"),
                Type3.Literal("dash"),
                Type3.Literal("dotted")
              ])
            ),
            color: Type3.Optional(
              Type3.String({ description: "Underline color (hex)" })
            )
          },
          { additionalProperties: false }
        )
      ])
    ),
    strike: Type3.Optional(Type3.Boolean({ description: "Strikethrough text" })),
    language: Type3.Optional(
      Type3.String({
        pattern: "^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$",
        description: 'Language tag (BCP-47, e.g. "en-US", "fr-FR") for spell-checking this text. Overrides the presentation default.',
        examples: ["en-US", "fr-FR", "de-DE", "it-IT", "es-ES"]
      })
    ),
    align: Type3.Optional(PptxAlignmentSchema),
    valign: Type3.Optional(VerticalAlignmentSchema),
    breakLine: Type3.Optional(
      Type3.Boolean({ description: "Add line break after text" })
    ),
    bullet: Type3.Optional(
      Type3.Union([
        Type3.Boolean({ description: "Enable default bullet" }),
        Type3.Object(
          {
            type: Type3.Optional(
              Type3.Union([Type3.Literal("bullet"), Type3.Literal("number")])
            ),
            style: Type3.Optional(
              Type3.String({ description: "Bullet character or style" })
            ),
            startAt: Type3.Optional(
              Type3.Number({ description: "Starting number for numbered lists" })
            )
          },
          { additionalProperties: false }
        )
      ])
    ),
    margin: Type3.Optional(
      Type3.Union([
        Type3.Number({ description: "Margin in points (all sides)" }),
        Type3.Array(Type3.Number(), {
          description: "Margins as [top, right, bottom, left] in points",
          minItems: 4,
          maxItems: 4
        })
      ])
    ),
    rotate: Type3.Optional(
      Type3.Number({ description: "Rotation angle in degrees" })
    ),
    shadow: Type3.Optional(ShadowSchema),
    fill: Type3.Optional(
      Type3.Object(
        {
          color: Type3.String({ description: "Fill color (hex without #)" }),
          transparency: Type3.Optional(
            Type3.Number({
              minimum: 0,
              maximum: 100,
              description: "Fill transparency (0-100)"
            })
          )
        },
        { additionalProperties: false }
      )
    ),
    hyperlink: Type3.Optional(
      Type3.Object(
        {
          url: Type3.Optional(Type3.String({ description: "Hyperlink URL" })),
          slide: Type3.Optional(
            Type3.Number({ description: "Slide number to link to" })
          ),
          tooltip: Type3.Optional(
            Type3.String({ description: "Hyperlink tooltip" })
          )
        },
        { additionalProperties: false }
      )
    ),
    lineSpacing: Type3.Optional(
      Type3.Number({ description: "Line spacing in points" })
    ),
    lineSpacingMultiple: Type3.Optional(
      Type3.Number({
        minimum: 0.1,
        maximum: 10,
        description: "Line spacing as a multiple of the font size (e.g. 0.9 = 90%). Takes precedence over lineSpacing."
      })
    ),
    charSpacing: Type3.Optional(
      Type3.Number({
        description: "Character spacing in points (positive = wider, negative = tighter)"
      })
    ),
    paraSpaceBefore: Type3.Optional(
      Type3.Number({ description: "Space before paragraph in points" })
    ),
    paraSpaceAfter: Type3.Optional(
      Type3.Number({ description: "Space after paragraph in points" })
    ),
    grid: Type3.Optional(GridPositionSchema),
    style: Type3.Optional(StyleNameSchema)
  },
  {
    description: "Text component props",
    additionalProperties: false
  }
);

// src/schemas/slide-content/image.ts
import { Type as Type4 } from "@sinclair/typebox";
var PptxImagePropsSchema = Type4.Object(
  {
    path: Type4.Optional(
      Type4.String({
        description: "Image file path or URL (mutually exclusive with base64 and svg)"
      })
    ),
    base64: Type4.Optional(
      Type4.String({
        description: "Base64-encoded image data in data URI format (mutually exclusive with path and svg)"
      })
    ),
    svg: Type4.Optional(
      Type4.String({
        description: 'Raw inline SVG markup, e.g. "<svg xmlns=\\"http://www.w3.org/2000/svg\\" viewBox=\\"0 0 24 24\\">...</svg>" (mutually exclusive with path and base64). Wrapped into an image/svg+xml data URI and embedded as a vector (PowerPoint 2016+); intrinsic size taken from the SVG viewBox/width/height when w/h omitted.'
      })
    ),
    x: Type4.Optional(
      Type4.Union([
        Type4.Number({ description: "X position in inches" }),
        Type4.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "X as percentage"
        })
      ])
    ),
    y: Type4.Optional(
      Type4.Union([
        Type4.Number({ description: "Y position in inches" }),
        Type4.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Y as percentage"
        })
      ])
    ),
    w: Type4.Optional(
      Type4.Union([
        Type4.Number({ description: "Width in inches" }),
        Type4.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Width as percentage"
        })
      ])
    ),
    h: Type4.Optional(
      Type4.Union([
        Type4.Number({ description: "Height in inches" }),
        Type4.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Height as percentage"
        })
      ])
    ),
    sizing: Type4.Optional(
      Type4.Object(
        {
          type: Type4.Union(
            [
              Type4.Literal("contain"),
              Type4.Literal("cover"),
              Type4.Literal("crop")
            ],
            { description: "Image sizing strategy" }
          ),
          w: Type4.Optional(
            Type4.Number({ description: "Target width in inches" })
          ),
          h: Type4.Optional(
            Type4.Number({ description: "Target height in inches" })
          )
        },
        { description: "Image sizing options", additionalProperties: false }
      )
    ),
    rotate: Type4.Optional(
      Type4.Number({ description: "Rotation angle in degrees" })
    ),
    rounding: Type4.Optional(
      Type4.Boolean({ description: "Apply rounded corners to image" })
    ),
    shadow: Type4.Optional(ShadowSchema),
    hyperlink: Type4.Optional(
      Type4.Object(
        {
          url: Type4.Optional(Type4.String({ description: "Hyperlink URL" })),
          slide: Type4.Optional(
            Type4.Number({ description: "Slide number to link to" })
          ),
          tooltip: Type4.Optional(
            Type4.String({ description: "Hyperlink tooltip" })
          )
        },
        { additionalProperties: false }
      )
    ),
    alt: Type4.Optional(
      Type4.String({ description: "Alternative text for accessibility" })
    ),
    grid: Type4.Optional(GridPositionSchema)
  },
  {
    description: "PPTX image component props",
    additionalProperties: false
  }
);

// src/schemas/slide-content/shape.ts
import { Type as Type5 } from "@sinclair/typebox";
var ShapeTypeSchema = Type5.Union(
  [
    Type5.Literal("rect"),
    Type5.Literal("roundRect"),
    Type5.Literal("ellipse"),
    Type5.Literal("triangle"),
    Type5.Literal("diamond"),
    Type5.Literal("pentagon"),
    Type5.Literal("hexagon"),
    Type5.Literal("star5"),
    Type5.Literal("star6"),
    Type5.Literal("line"),
    Type5.Literal("arc"),
    Type5.Literal("pie"),
    Type5.Literal("blockArc"),
    Type5.Literal("chord"),
    Type5.Literal("arrow"),
    Type5.Literal("chevron"),
    Type5.Literal("cloud"),
    Type5.Literal("heart"),
    Type5.Literal("lightning")
  ],
  { description: "Shape type" }
);
var TextSegmentSchema = Type5.Object(
  {
    text: Type5.String(),
    fontSize: Type5.Optional(Type5.Number({ minimum: 1 })),
    fontFace: Type5.Optional(Type5.String()),
    color: Type5.Optional(
      Type5.String({
        description: "Segment color (hex without # or semantic name)"
      })
    ),
    bold: Type5.Optional(Type5.Boolean()),
    fontWeight: Type5.Optional(Type5.Integer({ minimum: 100, maximum: 900 })),
    italic: Type5.Optional(Type5.Boolean()),
    breakLine: Type5.Optional(
      Type5.Boolean({ description: "Insert line break after this segment" })
    ),
    spaceBefore: Type5.Optional(
      Type5.Number({
        minimum: 0,
        description: "Space before paragraph in points"
      })
    ),
    spaceAfter: Type5.Optional(
      Type5.Number({
        minimum: 0,
        description: "Space after paragraph in points"
      })
    ),
    charSpacing: Type5.Optional(
      Type5.Number({ description: "Character spacing in points" })
    )
  },
  { additionalProperties: false }
);
var PATTERN_FILL_PRESETS = [
  "pct5",
  "pct10",
  "pct20",
  "pct25",
  "pct30",
  "pct40",
  "pct50",
  "pct60",
  "pct70",
  "pct75",
  "pct80",
  "pct90",
  "horz",
  "vert",
  "ltHorz",
  "ltVert",
  "dkHorz",
  "dkVert",
  "narHorz",
  "narVert",
  "dashHorz",
  "dashVert",
  "cross",
  "dnDiag",
  "upDiag",
  "ltDnDiag",
  "ltUpDiag",
  "dkDnDiag",
  "dkUpDiag",
  "wdDnDiag",
  "wdUpDiag",
  "dashDnDiag",
  "dashUpDiag",
  "diagCross",
  "smCheck",
  "lgCheck",
  "smGrid",
  "lgGrid",
  "dotGrid",
  "smConfetti",
  "lgConfetti",
  "horzBrick",
  "diagBrick",
  "solidDmnd",
  "openDmnd",
  "dotDmnd",
  "plaid",
  "sphere",
  "weave",
  "divot",
  "shingle",
  "wave",
  "trellis",
  "zigZag"
];
var GradientStopSchema = Type5.Object(
  {
    color: Type5.String({
      description: "Stop color (hex without # or semantic theme name)"
    }),
    pos: Type5.Number({
      minimum: 0,
      maximum: 100,
      description: "Stop position along the gradient (0-100)"
    }),
    transparency: Type5.Optional(
      Type5.Number({
        minimum: 0,
        maximum: 100,
        description: "Stop transparency (0-100)"
      })
    )
  },
  { additionalProperties: false, description: "Gradient color stop" }
);
var GradientFillSchema = Type5.Object(
  {
    type: Type5.Union([Type5.Literal("linear"), Type5.Literal("radial")], {
      description: "Gradient type"
    }),
    angle: Type5.Optional(
      Type5.Number({
        minimum: 0,
        maximum: 360,
        description: "Gradient angle in degrees for linear gradients (0 = left\u2192right, 90 = top\u2192bottom). Default: 0."
      })
    ),
    stops: Type5.Array(GradientStopSchema, {
      minItems: 2,
      description: "Gradient color stops (at least 2)"
    }),
    focus: Type5.Optional(
      Type5.Union(
        [
          Type5.Literal("center"),
          Type5.Literal("topLeft"),
          Type5.Literal("topRight"),
          Type5.Literal("bottomLeft"),
          Type5.Literal("bottomRight")
        ],
        {
          description: 'Focus point for radial gradients (default: "center")'
        }
      )
    )
  },
  { additionalProperties: false, description: "Gradient fill configuration" }
);
var PatternFillSchema = Type5.Object(
  {
    preset: Type5.Union(
      PATTERN_FILL_PRESETS.map((p) => Type5.Literal(p)),
      { description: "OOXML pattern preset name (a:pattFill prst value)" }
    ),
    foreground: Type5.String({
      description: "Pattern foreground color (hex without # or semantic name)"
    }),
    background: Type5.String({
      description: "Pattern background color (hex without # or semantic name)"
    })
  },
  { additionalProperties: false, description: "Pattern fill configuration" }
);
var ShapePropsSchema = Type5.Object(
  {
    type: ShapeTypeSchema,
    x: Type5.Optional(
      Type5.Union([
        Type5.Number({ description: "X position in inches" }),
        Type5.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "X as percentage"
        })
      ])
    ),
    y: Type5.Optional(
      Type5.Union([
        Type5.Number({ description: "Y position in inches" }),
        Type5.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Y as percentage"
        })
      ])
    ),
    w: Type5.Optional(
      Type5.Union([
        Type5.Number({ description: "Width in inches" }),
        Type5.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Width as percentage"
        })
      ])
    ),
    h: Type5.Optional(
      Type5.Union([
        Type5.Number({ description: "Height in inches" }),
        Type5.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Height as percentage"
        })
      ])
    ),
    fill: Type5.Optional(
      Type5.Object(
        {
          color: Type5.Optional(
            Type5.String({ description: "Fill color (hex without #)" })
          ),
          transparency: Type5.Optional(
            Type5.Number({
              minimum: 0,
              maximum: 100,
              description: "Fill transparency (0-100)"
            })
          ),
          gradient: Type5.Optional(GradientFillSchema),
          pattern: Type5.Optional(PatternFillSchema)
        },
        { additionalProperties: false }
      )
    ),
    line: Type5.Optional(
      Type5.Object(
        {
          color: Type5.Optional(
            Type5.String({ description: "Line color (hex without #)" })
          ),
          width: Type5.Optional(
            Type5.Number({ minimum: 0, description: "Line width in points" })
          ),
          dashType: Type5.Optional(
            Type5.Union([
              Type5.Literal("solid"),
              Type5.Literal("dash"),
              Type5.Literal("dot"),
              Type5.Literal("dashDot")
            ])
          )
        },
        { additionalProperties: false }
      )
    ),
    text: Type5.Optional(
      Type5.Union(
        [
          Type5.String({ description: "Plain text" }),
          Type5.Array(TextSegmentSchema, {
            description: "Rich text segments with per-segment formatting"
          })
        ],
        { description: "Text content inside the shape" }
      )
    ),
    fontSize: Type5.Optional(
      Type5.Number({ minimum: 1, description: "Font size for shape text" })
    ),
    fontFace: Type5.Optional(
      Type5.String({ description: "Font family for shape text" })
    ),
    fontColor: Type5.Optional(
      Type5.String({ description: "Font color for shape text (hex without #)" })
    ),
    charSpacing: Type5.Optional(
      Type5.Number({ description: "Character spacing in points for shape text" })
    ),
    bold: Type5.Optional(Type5.Boolean({ description: "Bold shape text" })),
    fontWeight: Type5.Optional(
      Type5.Integer({
        minimum: 100,
        maximum: 900,
        description: "Per-shape weight (100\u2013900). Overrides `bold` when set."
      })
    ),
    italic: Type5.Optional(Type5.Boolean({ description: "Italic shape text" })),
    align: Type5.Optional(PptxAlignmentSchema),
    valign: Type5.Optional(VerticalAlignmentSchema),
    rotate: Type5.Optional(
      Type5.Number({ description: "Rotation angle in degrees" })
    ),
    angleRange: Type5.Optional(
      Type5.Array(Type5.Number(), {
        minItems: 2,
        maxItems: 2,
        description: "Shape arc [start, end] angles in degrees (arc/pie/blockArc/chord shapes)"
      })
    ),
    flipH: Type5.Optional(
      Type5.Boolean({ description: "Flip shape horizontally" })
    ),
    flipV: Type5.Optional(
      Type5.Boolean({ description: "Flip shape vertically" })
    ),
    shadow: Type5.Optional(ShadowSchema),
    rectRadius: Type5.Optional(
      Type5.Number({
        minimum: 0,
        description: "Corner radius for roundRect shape in inches"
      })
    ),
    grid: Type5.Optional(GridPositionSchema),
    style: Type5.Optional(StyleNameSchema)
  },
  {
    description: "Shape component props",
    additionalProperties: false
  }
);

// src/schemas/slide-content/table.ts
import { Type as Type6 } from "@sinclair/typebox";
var PptxTableCellSchema = Type6.Union([
  Type6.String({ description: "Simple text cell" }),
  Type6.Object(
    {
      text: Type6.String({ description: "Cell text content" }),
      color: Type6.Optional(
        Type6.String({ description: "Text color (hex without #)" })
      ),
      fill: Type6.Optional(
        Type6.String({ description: "Cell background color (hex without #)" })
      ),
      fontSize: Type6.Optional(
        Type6.Number({ description: "Font size in points" })
      ),
      fontFace: Type6.Optional(Type6.String({ description: "Font family" })),
      bold: Type6.Optional(Type6.Boolean({ description: "Bold text" })),
      fontWeight: Type6.Optional(
        Type6.Integer({
          minimum: 100,
          maximum: 900,
          description: "Per-cell weight (100\u2013900). Overrides `bold` when set."
        })
      ),
      italic: Type6.Optional(Type6.Boolean({ description: "Italic text" })),
      align: Type6.Optional(PptxAlignmentSchema),
      valign: Type6.Optional(VerticalAlignmentSchema),
      colspan: Type6.Optional(
        Type6.Number({ minimum: 1, description: "Column span" })
      ),
      rowspan: Type6.Optional(
        Type6.Number({ minimum: 1, description: "Row span" })
      ),
      margin: Type6.Optional(
        Type6.Union([
          Type6.Number({ description: "Margin in points (all sides)" }),
          Type6.Array(Type6.Number(), { minItems: 4, maxItems: 4 })
        ])
      )
    },
    { additionalProperties: false }
  )
]);
var PptxTablePropsSchema = Type6.Object(
  {
    rows: Type6.Array(
      Type6.Array(PptxTableCellSchema, { description: "Row of cells" }),
      { description: "Table rows (array of arrays)", minItems: 1 }
    ),
    x: Type6.Optional(
      Type6.Union([
        Type6.Number({ description: "X position in inches" }),
        Type6.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "X as percentage"
        })
      ])
    ),
    y: Type6.Optional(
      Type6.Union([
        Type6.Number({ description: "Y position in inches" }),
        Type6.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Y as percentage"
        })
      ])
    ),
    w: Type6.Optional(
      Type6.Union([
        Type6.Number({ description: "Width in inches" }),
        Type6.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Width as percentage"
        })
      ])
    ),
    h: Type6.Optional(
      Type6.Union([
        Type6.Number({ description: "Height in inches" }),
        Type6.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Height as percentage"
        })
      ])
    ),
    colW: Type6.Optional(
      Type6.Union([
        Type6.Number({ description: "Uniform column width in inches" }),
        Type6.Array(Type6.Number(), {
          description: "Individual column widths in inches"
        })
      ])
    ),
    rowH: Type6.Optional(
      Type6.Union([
        Type6.Number({ description: "Uniform row height in inches" }),
        Type6.Array(Type6.Number(), {
          description: "Individual row heights in inches"
        })
      ])
    ),
    border: Type6.Optional(
      Type6.Object(
        {
          type: Type6.Optional(
            Type6.Union([
              Type6.Literal("solid"),
              Type6.Literal("dash"),
              Type6.Literal("dot"),
              Type6.Literal("none")
            ])
          ),
          pt: Type6.Optional(
            Type6.Number({ minimum: 0, description: "Border width in points" })
          ),
          color: Type6.Optional(
            Type6.String({ description: "Border color (hex without #)" })
          )
        },
        { additionalProperties: false }
      )
    ),
    fill: Type6.Optional(
      Type6.String({ description: "Table background color (hex without #)" })
    ),
    fontSize: Type6.Optional(
      Type6.Number({
        minimum: 1,
        description: "Default font size for all cells"
      })
    ),
    fontFace: Type6.Optional(
      Type6.String({ description: "Default font family for all cells" })
    ),
    fontWeight: Type6.Optional(
      Type6.Integer({
        minimum: 100,
        maximum: 900,
        description: "Default weight for all cells (100\u2013900). Same sub-family aliasing as the per-cell `fontWeight`; a cell that sets its own `fontWeight` or `bold` opts out."
      })
    ),
    color: Type6.Optional(
      Type6.String({
        description: "Default text color for all cells (hex without #)"
      })
    ),
    align: Type6.Optional(PptxAlignmentSchema),
    valign: Type6.Optional(VerticalAlignmentSchema),
    headerRow: Type6.Optional(
      Type6.Boolean({
        description: "Style the first row as a header \u2014 bold, on the theme's secondary background. Cells that state their own `bold`, `fill` or `color` keep it. Unlike the DOCX table, which declares its headers structurally, a PPTX table is rows of cells and the first one is a header only because you say so."
      })
    ),
    autoPage: Type6.Optional(
      Type6.Boolean({
        description: "Auto-paginate table across multiple slides when content overflows"
      })
    ),
    autoPageRepeatHeader: Type6.Optional(
      Type6.Boolean({
        description: "Repeat first row as header on each auto-paged slide"
      })
    ),
    margin: Type6.Optional(
      Type6.Union([
        Type6.Number({ description: "Cell margin in points (all sides)" }),
        Type6.Array(Type6.Number(), { minItems: 4, maxItems: 4 })
      ])
    ),
    borderRadius: Type6.Optional(
      Type6.Number({
        minimum: 0,
        description: "Rounded corner radius in inches. Renders a roundRect shape behind the table."
      })
    ),
    grid: Type6.Optional(GridPositionSchema)
  },
  {
    description: "PPTX table component props",
    additionalProperties: false
  }
);

// src/schemas/slide-content/highcharts.ts
import { Type as Type7 } from "@sinclair/typebox";
var PptxHighchartsPropsSchema = Type7.Object(
  {
    options: Type7.Intersect([
      Type7.Record(Type7.String(), Type7.Unknown()),
      Type7.Object({
        chart: Type7.Object({
          width: Type7.Number(),
          height: Type7.Number()
        })
      })
    ]),
    scale: Type7.Optional(Type7.Number()),
    serverUrl: Type7.Optional(
      Type7.String({
        description: "Highcharts Export Server URL (default: http://localhost:7801)"
      })
    ),
    // Optional resources forwarded verbatim to the export server (CSS/JS/files).
    // Notably enables @font-face rules so charts render in custom fonts.
    resources: Type7.Optional(
      Type7.Object({
        css: Type7.Optional(Type7.String()),
        js: Type7.Optional(Type7.String()),
        files: Type7.Optional(Type7.Array(Type7.String()))
      })
    ),
    x: Type7.Optional(
      Type7.Union([
        Type7.Number({ description: "X position in inches" }),
        Type7.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "X as percentage"
        })
      ])
    ),
    y: Type7.Optional(
      Type7.Union([
        Type7.Number({ description: "Y position in inches" }),
        Type7.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Y as percentage"
        })
      ])
    ),
    w: Type7.Optional(
      Type7.Union([
        Type7.Number({ description: "Width in inches" }),
        Type7.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Width as percentage"
        })
      ])
    ),
    h: Type7.Optional(
      Type7.Union([
        Type7.Number({ description: "Height in inches" }),
        Type7.String({
          pattern: "^\\d+(\\.\\d+)?%$",
          description: "Height as percentage"
        })
      ])
    ),
    grid: Type7.Optional(GridPositionSchema)
  },
  {
    description: "PPTX Highcharts component props",
    additionalProperties: false
  }
);

// src/schemas/slide-content/chart.ts
import { Type as Type8 } from "@sinclair/typebox";
var positionValue = (inches, percent) => Type8.Union([
  Type8.Number({ description: inches }),
  Type8.String({
    pattern: "^\\d+(\\.\\d+)?%$",
    description: percent
  })
]);
var ChartTypeSchema = Type8.Union(
  [
    Type8.Literal("area"),
    Type8.Literal("bar"),
    Type8.Literal("bar3D"),
    Type8.Literal("bubble"),
    Type8.Literal("doughnut"),
    Type8.Literal("line"),
    Type8.Literal("pie"),
    Type8.Literal("radar"),
    Type8.Literal("scatter")
  ],
  { description: "Chart type" }
);
var ChartDataSeriesSchema = Type8.Object(
  {
    name: Type8.Optional(Type8.String({ description: "Series name" })),
    labels: Type8.Optional(
      Type8.Array(Type8.String(), {
        description: "Category labels. Needed on every series, not just the first, and the same length as `values`; a series without both `labels` and `values` drops the whole chart."
      })
    ),
    values: Type8.Optional(
      Type8.Array(Type8.Number(), {
        description: "Data values, one per label. Needed on every series \u2014 see `labels`."
      })
    ),
    sizes: Type8.Optional(
      Type8.Array(Type8.Number(), {
        description: "Bubble sizes (bubble charts only)"
      })
    )
  },
  { additionalProperties: false }
);
var ChartGridLineSchema = Type8.Object(
  {
    style: Type8.Optional(
      Type8.Union(
        [
          Type8.Literal("solid"),
          Type8.Literal("dash"),
          Type8.Literal("dot"),
          Type8.Literal("none")
        ],
        { description: "Grid line style" }
      )
    ),
    size: Type8.Optional(
      Type8.Number({ minimum: 0, description: "Grid line width (points)" })
    ),
    color: Type8.Optional(
      Type8.String({ description: "Grid line color (hex or semantic)" })
    )
  },
  { additionalProperties: false, description: "Axis grid line styling" }
);
var PptxChartPropsSchema = Type8.Object(
  {
    type: ChartTypeSchema,
    data: Type8.Array(ChartDataSeriesSchema, {
      description: "Chart data series",
      minItems: 1
    }),
    // Display toggles
    showLegend: Type8.Optional(
      Type8.Boolean({ description: "Show chart legend" })
    ),
    showTitle: Type8.Optional(Type8.Boolean({ description: "Show chart title" })),
    showValue: Type8.Optional(Type8.Boolean({ description: "Show data values" })),
    showPercent: Type8.Optional(
      Type8.Boolean({ description: "Show percentages (pie/doughnut)" })
    ),
    showLabel: Type8.Optional(
      Type8.Boolean({ description: "Show category labels on data points" })
    ),
    showSerName: Type8.Optional(
      Type8.Boolean({ description: "Show series name on data points" })
    ),
    // Title
    title: Type8.Optional(Type8.String({ description: "Chart title text" })),
    titleFontSize: Type8.Optional(
      Type8.Number({ description: "Title font size (points)" })
    ),
    titleColor: Type8.Optional(
      Type8.String({ description: "Title color (hex or semantic)" })
    ),
    titleFontFace: Type8.Optional(
      Type8.String({ description: "Title font face" })
    ),
    titleFontWeight: Type8.Optional(
      Type8.Integer({
        minimum: 100,
        maximum: 900,
        description: "Title weight (100\u2013900). Rendered as a sub-family alias \u2014 see `dataLabelFontWeight`."
      })
    ),
    // Chart colors
    chartColors: Type8.Optional(
      Type8.Array(Type8.String(), {
        description: "Series colors (hex or semantic theme names). Defaults to theme palette."
      })
    ),
    // Data element border (bars/slices/areas)
    dataBorder: Type8.Optional(
      Type8.Object(
        {
          pt: Type8.Number({
            minimum: 0,
            description: "Border width (points)"
          }),
          color: Type8.String({
            description: "Border color (hex or semantic)"
          })
        },
        {
          additionalProperties: false,
          description: "Outline on data elements (bars, slices, areas)"
        }
      )
    ),
    // Legend
    legendPos: Type8.Optional(
      Type8.Union(
        [
          Type8.Literal("b"),
          Type8.Literal("l"),
          Type8.Literal("r"),
          Type8.Literal("t"),
          Type8.Literal("tr")
        ],
        { description: "Legend position" }
      )
    ),
    legendFontSize: Type8.Optional(
      Type8.Number({ description: "Legend font size" })
    ),
    legendFontFace: Type8.Optional(
      Type8.String({ description: "Legend font face" })
    ),
    legendFontWeight: Type8.Optional(
      Type8.Integer({
        minimum: 100,
        maximum: 900,
        description: "Legend weight (100\u2013900). Rendered as a sub-family alias \u2014 see `dataLabelFontWeight`. PowerPoint gives the legend no bold toggle, so 400 and 700 both render Regular (700 warns)."
      })
    ),
    legendColor: Type8.Optional(
      Type8.String({ description: "Legend text color" })
    ),
    // Category axis
    catAxisTitle: Type8.Optional(
      Type8.String({ description: "Category axis title" })
    ),
    catAxisTitleFontSize: Type8.Optional(
      Type8.Number({
        minimum: 1,
        description: "Category axis title font size (points). Defaults to the axis label size."
      })
    ),
    catAxisTitleFontFace: Type8.Optional(
      Type8.String({
        description: "Category axis title font face. Defaults to the axis label face, else the theme body font."
      })
    ),
    catAxisTitleColor: Type8.Optional(
      Type8.String({
        description: "Category axis title color (hex or semantic). Defaults to the axis label color."
      })
    ),
    catAxisTitleRotate: Type8.Optional(
      Type8.Number({
        minimum: -360,
        maximum: 360,
        description: "Category axis title rotation (degrees). Omit for the PowerPoint default."
      })
    ),
    catAxisHidden: Type8.Optional(
      Type8.Boolean({ description: "Hide category axis" })
    ),
    catAxisLabelRotate: Type8.Optional(
      Type8.Number({ description: "Category axis label rotation (degrees)" })
    ),
    catAxisLabelFontSize: Type8.Optional(
      Type8.Number({ description: "Category axis label font size" })
    ),
    catAxisLabelColor: Type8.Optional(
      Type8.String({
        description: "Category axis label color (hex or semantic)"
      })
    ),
    catAxisLabelFontFace: Type8.Optional(
      Type8.String({ description: "Category axis label font face" })
    ),
    catAxisLabelFontWeight: Type8.Optional(
      Type8.Integer({
        minimum: 100,
        maximum: 900,
        description: "Category axis label weight (100\u2013900). Rendered as a sub-family alias \u2014 see `dataLabelFontWeight`."
      })
    ),
    catGridLine: Type8.Optional(ChartGridLineSchema),
    // Value axis
    valAxisTitle: Type8.Optional(
      Type8.String({ description: "Value axis title" })
    ),
    valAxisTitleFontSize: Type8.Optional(
      Type8.Number({
        minimum: 1,
        description: "Value axis title font size (points). Defaults to the axis label size."
      })
    ),
    valAxisTitleFontFace: Type8.Optional(
      Type8.String({
        description: "Value axis title font face. Defaults to the axis label face, else the theme body font."
      })
    ),
    valAxisTitleColor: Type8.Optional(
      Type8.String({
        description: "Value axis title color (hex or semantic). Defaults to the axis label color."
      })
    ),
    valAxisTitleRotate: Type8.Optional(
      Type8.Number({
        minimum: -360,
        maximum: 360,
        description: "Value axis title rotation (degrees), e.g. -90 to read bottom to top. Omit for the PowerPoint default."
      })
    ),
    valAxisHidden: Type8.Optional(
      Type8.Boolean({ description: "Hide value axis" })
    ),
    valAxisMinVal: Type8.Optional(
      Type8.Number({ description: "Value axis minimum" })
    ),
    valAxisMaxVal: Type8.Optional(
      Type8.Number({ description: "Value axis maximum" })
    ),
    valAxisLabelFormatCode: Type8.Optional(
      Type8.String({
        description: 'Value axis label format (e.g. "$0.00", "#%")'
      })
    ),
    valAxisMajorUnit: Type8.Optional(
      Type8.Number({ description: "Value axis major unit / tick interval" })
    ),
    valAxisLabelColor: Type8.Optional(
      Type8.String({ description: "Value axis label color (hex or semantic)" })
    ),
    valAxisLabelFontFace: Type8.Optional(
      Type8.String({ description: "Value axis label font face" })
    ),
    valAxisLabelFontWeight: Type8.Optional(
      Type8.Integer({
        minimum: 100,
        maximum: 900,
        description: "Value axis label weight (100\u2013900). Rendered as a sub-family alias \u2014 see `dataLabelFontWeight`."
      })
    ),
    valAxisLabelFontSize: Type8.Optional(
      Type8.Number({ description: "Value axis label font size" })
    ),
    valGridLine: Type8.Optional(ChartGridLineSchema),
    catAxisLineShow: Type8.Optional(
      Type8.Boolean({ description: "Show the category axis line" })
    ),
    valAxisLineShow: Type8.Optional(
      Type8.Boolean({ description: "Show the value axis line" })
    ),
    // Bar-specific
    barDir: Type8.Optional(
      Type8.Union([Type8.Literal("bar"), Type8.Literal("col")], {
        description: 'Bar direction: "bar" (horizontal) or "col" (vertical, default)'
      })
    ),
    barGrouping: Type8.Optional(
      Type8.Union(
        [
          Type8.Literal("clustered"),
          Type8.Literal("stacked"),
          Type8.Literal("percentStacked")
        ],
        { description: "Bar grouping style" }
      )
    ),
    barGapWidthPct: Type8.Optional(
      Type8.Number({
        minimum: 0,
        maximum: 500,
        description: "Bar gap width (0-500%)"
      })
    ),
    barOverlapPct: Type8.Optional(
      Type8.Number({
        minimum: -100,
        maximum: 100,
        description: "Overlap between series bars (-100 to 100%). 100 = fully overlapped, negative = gap."
      })
    ),
    // Line-specific
    lineSmooth: Type8.Optional(Type8.Boolean({ description: "Smooth lines" })),
    lineDataSymbol: Type8.Optional(
      Type8.Union(
        [
          Type8.Literal("circle"),
          Type8.Literal("dash"),
          Type8.Literal("diamond"),
          Type8.Literal("dot"),
          Type8.Literal("none"),
          Type8.Literal("square"),
          Type8.Literal("triangle")
        ],
        { description: "Line data point marker symbol" }
      )
    ),
    lineSize: Type8.Optional(
      Type8.Number({ description: "Line width (points)" })
    ),
    lineDataSymbolSize: Type8.Optional(
      Type8.Number({
        minimum: 2,
        maximum: 72,
        description: "Line data point marker size (points)"
      })
    ),
    // Pie/doughnut-specific
    firstSliceAng: Type8.Optional(
      Type8.Number({
        minimum: 0,
        maximum: 359,
        description: "Angle of first slice (degrees)"
      })
    ),
    holeSize: Type8.Optional(
      Type8.Number({
        minimum: 10,
        maximum: 90,
        description: "Doughnut hole size (%)"
      })
    ),
    // Radar-specific
    radarStyle: Type8.Optional(
      Type8.Union(
        [
          Type8.Literal("standard"),
          Type8.Literal("marker"),
          Type8.Literal("filled")
        ],
        { description: "Radar chart style" }
      )
    ),
    // Data labels
    dataLabelColor: Type8.Optional(
      Type8.String({ description: "Data label text color" })
    ),
    dataLabelFontSize: Type8.Optional(
      Type8.Number({ description: "Data label font size" })
    ),
    dataLabelFontFace: Type8.Optional(
      Type8.String({ description: "Data label font face" })
    ),
    dataLabelFontWeight: Type8.Optional(
      Type8.Integer({
        minimum: 100,
        maximum: 900,
        description: 'Data label weight (100\u2013900); overrides `dataLabelFontBold`. PowerPoint chart labels carry no numeric weight, so a non-RIBBI weight renders by rewriting the font face to the matching sub-family ("Inter" at 300 \u2192 "Inter Light") \u2014 400 and 700 stay on the family and use the bold toggle. Falls back to the theme body font when the sibling font face is unset.'
      })
    ),
    dataLabelFontBold: Type8.Optional(
      Type8.Boolean({ description: "Bold data labels" })
    ),
    dataLabelPosition: Type8.Optional(
      Type8.Union(
        [
          Type8.Literal("b"),
          Type8.Literal("bestFit"),
          Type8.Literal("ctr"),
          Type8.Literal("l"),
          Type8.Literal("r"),
          Type8.Literal("t"),
          Type8.Literal("inEnd"),
          Type8.Literal("outEnd")
        ],
        { description: "Data label position" }
      )
    ),
    // Positioning
    x: Type8.Optional(positionValue("X position in inches", "X as percentage")),
    y: Type8.Optional(positionValue("Y position in inches", "Y as percentage")),
    w: Type8.Optional(positionValue("Width in inches", "Width as percentage")),
    h: Type8.Optional(positionValue("Height in inches", "Height as percentage")),
    grid: Type8.Optional(GridPositionSchema)
  },
  {
    description: "Native PowerPoint chart component props",
    additionalProperties: false
  }
);

// src/schemas/slide-content.ts
function pptxComponentRequiresProps(component) {
  if (component.propsRequired !== void 0) return component.propsRequired;
  const schema = component.propsSchema;
  return schema.type !== "object" || (schema.required?.length ?? 0) > 0;
}
var PPTX_SLIDE_CONTENT_COMPONENTS = [
  {
    name: "text",
    propsSchema: TextPropsSchema,
    hasChildren: false,
    category: "content",
    // `text` XOR `runs`: both optional in the schema, one of them mandatory in
    // fact (validation/text-content-conflicts.ts rejects neither and both).
    propsRequired: true,
    description: "Text element - displays text with formatting, positioning and styling options. Prefer the named `style` values (title, heading1, body\u2026) and theme color tokens over raw fontSize/hex \u2014 a theme swap must restyle the deck without editing every node."
  },
  {
    name: "image",
    propsSchema: PptxImagePropsSchema,
    hasChildren: false,
    category: "content",
    // One of `path`/`base64`/`svg`, so same shape as `text`: a source is
    // mandatory in fact and optional in the schema. Unlike `text` this is only
    // half enforced — the missing key is caught, an empty props object is not,
    // because `image` has no analogue of validation/text-content-conflicts.ts
    // and a sourceless image is an IMAGE_NO_SOURCE warning at generation, not
    // an error. The flag is still not the guess: `image` has required `props`
    // in the published schema since it was first generated, so dropping it
    // here would loosen that contract rather than tighten the runtime.
    propsRequired: true,
    description: "Image element - displays images from file path, URL, or base64 data."
  },
  {
    name: "shape",
    propsSchema: ShapePropsSchema,
    hasChildren: false,
    category: "content",
    description: "Shape element - draws geometric shapes with optional text, fill, and line styling."
  },
  {
    name: "table",
    propsSchema: PptxTablePropsSchema,
    hasChildren: false,
    category: "content",
    description: "Table element, declared ROW-MAJOR: `props.rows[][]`, an array of rows, each an array of cells, with no structural header \u2014 `headerRow: true` styles the first row as one. Note the DOCX `table` is the other way round \u2014 columns each carrying their own header and cells \u2014 so a table cannot be moved between the formats unchanged."
  },
  {
    name: "highcharts",
    propsSchema: PptxHighchartsPropsSchema,
    hasChildren: false,
    category: "content",
    description: "Highcharts element - renders charts via Highcharts Export Server."
  },
  {
    name: "chart",
    propsSchema: PptxChartPropsSchema,
    hasChildren: false,
    category: "content",
    description: "Native PowerPoint chart - editable, scalable, no external server needed. Always set series colors (theme-derived, one accent per series) \u2014 the auto-palette reads as default Office."
  }
];
function createSlideContentComponentSchema(component) {
  return Type9.Object(
    {
      name: Type9.Literal(component.name),
      id: Type9.Optional(Type9.String()),
      enabled: Type9.Optional(
        Type9.Boolean({
          default: true,
          description: "When false, this component is filtered out and not rendered. Defaults to true."
        })
      ),
      props: pptxComponentRequiresProps(component) ? component.propsSchema : Type9.Optional(component.propsSchema)
    },
    { additionalProperties: false, description: component.description }
  );
}
var PptxSlideContentSchema = Type9.Union(
  [
    createSlideContentComponentSchema(PPTX_SLIDE_CONTENT_COMPONENTS[0]),
    createSlideContentComponentSchema(PPTX_SLIDE_CONTENT_COMPONENTS[1]),
    createSlideContentComponentSchema(PPTX_SLIDE_CONTENT_COMPONENTS[2]),
    createSlideContentComponentSchema(PPTX_SLIDE_CONTENT_COMPONENTS[3]),
    createSlideContentComponentSchema(PPTX_SLIDE_CONTENT_COMPONENTS[4]),
    createSlideContentComponentSchema(PPTX_SLIDE_CONTENT_COMPONENTS[5])
  ],
  {
    $id: "PptxSlideContent",
    discriminator: { propertyName: "name" },
    description: "A single PPTX slide content element (text, image, shape, table, highcharts, or chart)."
  }
);
export {
  ColorValueSchema,
  GradientFillSchema,
  GradientStopSchema,
  GridPositionSchema,
  PATTERN_FILL_PRESETS,
  PPTX_SLIDE_CONTENT_COMPONENTS,
  PatternFillSchema,
  PptxAlignmentSchema,
  PptxChartPropsSchema,
  PptxHighchartsPropsSchema,
  PptxImagePropsSchema,
  PptxSlideContentSchema,
  PptxTablePropsSchema,
  SEMANTIC_COLOR_ALIASES,
  SEMANTIC_COLOR_NAMES,
  STYLE_NAMES,
  ShadowSchema,
  ShapePropsSchema,
  ShapeTypeSchema,
  StyleNameSchema,
  TextPropsSchema,
  TextRunSchema,
  TextSegmentSchema,
  VerticalAlignmentSchema,
  pptxComponentRequiresProps
};
//# sourceMappingURL=slide-content.js.map